import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

const AnonymousUserContext = createContext()

export const useAnonymousUser = () => {
  const context = useContext(AnonymousUserContext)
  if (!context) {
    throw new Error('useAnonymousUser must be used within an AnonymousUserProvider')
  }
  return context
}

export const AnonymousUserProvider = ({ children }) => {
  const [anonymousUser, setAnonymousUser] = useState(null)
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [loading, setLoading] = useState(true)

  // Generar ID anónimo único
  const generateAnonymousId = useCallback(() => {
    // Combinar timestamp + random para asegurar unicidad
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substr(2, 5)
    return `anon_${timestamp}_${random}`
  }, [])

  // Generar nombre anónimo (6 dígitos)
  const generateAnonymousName = useCallback(() => {
    return Math.floor(100000 + Math.random() * 900000).toString()
  }, [])

  // Crear usuario anónimo
  const createAnonymousUser = useCallback(() => {
    const anonId = generateAnonymousId()
    const anonName = generateAnonymousName()

    const newAnonymousUser = {
      uid: anonId,
      isAnonymous: true,
      displayName: `Usuario ${anonName}`,
      email: null,
      photoURL: null,
      emailVerified: false,
      createdAt: new Date().toISOString(),
      lastSeenAt: new Date().toISOString(),
      sessionId: anonId,
      // Limitaciones del usuario anónimo
      limitations: {
        canCreateEvents: false,
        canCreateGroups: false,
        canUploadImages: false,
        canModerate: false,
        canAccessProfile: false,
        canSaveEvents: false,
        maxChatMessages: 100, // Límite de mensajes por sesión
        maxGroupJoins: 10 // Límite de grupos que puede unirse
      }
    }

    // Guardar en localStorage
    localStorage.setItem('wyra_anonymous_user', JSON.stringify(newAnonymousUser))

    setAnonymousUser(newAnonymousUser)
    setIsAnonymous(true)

    console.log('👤 Usuario anónimo creado:', anonName)
    return newAnonymousUser
  }, [generateAnonymousId, generateAnonymousName])

  // Inicializar usuario anónimo desde localStorage
  useEffect(() => {
    try {
      const storedAnonymous = localStorage.getItem('wyra_anonymous_user')

      if (storedAnonymous) {
        const parsedAnonymous = JSON.parse(storedAnonymous)

        // Verificar si la sesión es reciente (menos de 7 días)
        const createdAt = new Date(parsedAnonymous.createdAt)
        const now = new Date()
        const daysDiff = (now - createdAt) / (1000 * 60 * 60 * 24)

        if (daysDiff < 7) {
          setAnonymousUser(parsedAnonymous)
          setIsAnonymous(true)
          console.log('👤 Sesión anónima recuperada:', parsedAnonymous.displayName)
        } else {
          // Sesión expirada, limpiar
          localStorage.removeItem('wyra_anonymous_user')
        }
      }
    } catch (error) {
      console.warn('⚠️ Error recuperando sesión anónima:', error)
      localStorage.removeItem('wyra_anonymous_user')
    }

    setLoading(false)
  }, [])

  // Convertir anónimo a usuario registrado
  const convertToRegistered = useCallback((registeredUser) => {
    // Transferir datos del anónimo al registrado
    const transferData = {
      anonymousId: anonymousUser?.uid,
      previousChats: [], // Se debería cargar desde el anónimo
      previousGroups: [], // Se debería cargar desde el anónimo
      convertedAt: new Date().toISOString()
    }

    // Limpiar datos anónimos
    localStorage.removeItem('wyra_anonymous_user')
    setAnonymousUser(null)
    setIsAnonymous(false)

    console.log('✅ Usuario anónimo convertido a registrado')
    return transferData
  }, [anonymousUser])

  // Limpiar sesión anónima
  const clearAnonymousSession = useCallback(() => {
    localStorage.removeItem('wyra_anonymous_user')
    setAnonymousUser(null)
    setIsAnonymous(false)
    console.log('🧹 Sesión anónima limpiada')
  }, [])

  // Actualizar última actividad
  const updateLastSeen = useCallback(() => {
    if (anonymousUser && isAnonymous) {
      const updatedUser = {
        ...anonymousUser,
        lastSeenAt: new Date().toISOString()
      }
      setAnonymousUser(updatedUser)
      localStorage.setItem('wyra_anonymous_user', JSON.stringify(updatedUser))
    }
  }, [anonymousUser, isAnonymous])

  // Verificar si puede realizar una acción
  const canPerformAction = useCallback((action) => {
    if (!isAnonymous) return true // Usuarios registrados pueden todo

    return !anonymousUser?.limitations[action] !== false
  }, [isAnonymous, anonymousUser])

  // Obtener mensaje de limitación
  const getLimitationMessage = useCallback((action) => {
    const messages = {
      canCreateEvents: 'Para crear eventos necesitas registrar una cuenta gratuita',
      canCreateGroups: 'Para crear grupos necesitas registrar una cuenta gratuita',
      canUploadImages: 'Para subir imágenes necesitas registrar una cuenta gratuita',
      canAccessProfile: 'Para ver tu perfil necesitas registrar una cuenta gratuita',
      canSaveEvents: 'Para guardar eventos necesitas registrar una cuenta gratuita',
      canModerate: 'Para moderar contenido necesitas registrar una cuenta gratuita'
    }

    return messages[action] || 'Esta función requiere registro'
  }, [])

  // Auto-actualizar lastSeen cada 5 minutos
  useEffect(() => {
    if (!isAnonymous) return

    const interval = setInterval(updateLastSeen, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [isAnonymous, updateLastSeen])

  const value = {
    // Estado
    anonymousUser,
    isAnonymous,
    loading,

    // Acciones
    createAnonymousUser,
    convertToRegistered,
    clearAnonymousSession,
    updateLastSeen,

    // Utilidades
    canPerformAction,
    getLimitationMessage,
    generateAnonymousName
  }

  return (
    <AnonymousUserContext.Provider value={value}>
      {children}
    </AnonymousUserContext.Provider>
  )
}